'use client';
import React, { useState, useMemo } from 'react';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Download,
  AlertTriangle,
  CheckCircle2,
  Columns,
} from 'lucide-react';
import { MONTHLY_SUMMARIES, type MonthlyTeacherSummary } from '@/lib/mockData';
import { FlagBadge } from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';

type SortKey = keyof MonthlyTeacherSummary;
type SortDir = 'asc' | 'desc' | null;

const THRESHOLD = 90;

// Columns hidden by default on tablet/mobile
const SECONDARY_COLS = new Set(['totalDays', 'presentDays', 'absentDays', 'lateDays', 'leaveDays', 'deportmentViolations', 'avgCheckIn']);

export default function ReportSummaryTable() {
  const [search, setSearch] = useState('');
  const [flagFilter, setFlagFilter] = useState<string>('All');
  const [sortKey, setSortKey] = useState<SortKey>('attendanceRate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showSecondary, setShowSecondary] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let data = [...MONTHLY_SUMMARIES];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (t) =>
          t.teacherName.toLowerCase().includes(q) ||
          t.department.toLowerCase().includes(q)
      );
    }
    if (flagFilter !== 'All') {
      data = data.filter((t) => t.flag === flagFilter);
    }
    if (sortKey && sortDir) {
      data.sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDir === 'asc' ? av - bv : bv - av;
        }
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return data;
  }, [search, flagFilter, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    if (sortDir === 'asc') return <ChevronUp size={12} className="text-primary" />;
    if (sortDir === 'desc') return <ChevronDown size={12} className="text-primary" />;
    return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
  }

  function RateCell({ value }: { value: number }) {
    const below = value < THRESHOLD;
    return (
      <span
        className={`tabular-nums font-600 text-sm ${
          below ? 'text-red-600' : value >= 95 ? 'text-green-600' : 'text-foreground'
        }`}
        style={{ fontWeight: 600 }}
      >
        {value}%
        {below && <AlertTriangle size={11} className="inline ml-1 text-red-500" />}
      </span>
    );
  }

  const COLUMNS: { key: SortKey; label: string; sortable: boolean }[] = [
    { key: 'teacherName', label: 'Teacher', sortable: true },
    { key: 'department', label: 'Department', sortable: true },
    { key: 'totalDays', label: 'Days', sortable: true },
    { key: 'presentDays', label: 'Present', sortable: true },
    { key: 'absentDays', label: 'Absent', sortable: true },
    { key: 'lateDays', label: 'Late', sortable: true },
    { key: 'leaveDays', label: 'Leave', sortable: true },
    { key: 'attendanceRate', label: 'Attendance %', sortable: true },
    { key: 'punctualityRate', label: 'Punctuality %', sortable: true },
    { key: 'deportmentCompliance', label: 'Deportment %', sortable: true },
    { key: 'deportmentViolations', label: 'Violations', sortable: true },
    { key: 'avgCheckIn', label: 'Avg Check-In', sortable: true },
    { key: 'flag', label: 'Status', sortable: true },
  ];

  const visibleColumns = COLUMNS.filter(
    (col) => !SECONDARY_COLS.has(col.key) || showSecondary
  );

  return (
    <div className="bg-card border border-border rounded-xl">
      {/* Table Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-600 text-foreground" style={{ fontWeight: 600 }}>
            Per-Teacher Monthly Summary — May 2026
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {filtered.length} teacher{filtered.length !== 1 ? 's' : ''} &mdash; Red values indicate below 90% threshold
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 sm:flex-none">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search teacher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring w-full sm:w-44"
            />
          </div>
          <select
            value={flagFilter}
            onChange={(e) => setFlagFilter(e.target.value)}
            className="text-sm border border-border rounded-lg bg-background text-foreground px-2 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {['All', 'OK', 'At Risk', 'Critical'].map((f) => (
              <option key={`flag-filter-${f}`} value={f}>{f}</option>
            ))}
          </select>
          {/* Toggle secondary columns */}
          <button
            type="button"
            onClick={() => setShowSecondary((v) => !v)}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-500 transition-colors ${
              showSecondary
                ? 'border-primary bg-primary/10 text-primary' :'border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            style={{ fontWeight: 500 }}
            title="Toggle detail columns"
          >
            <Columns size={13} />
            {showSecondary ? 'Less' : 'Details'}
          </button>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card text-sm font-500 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            style={{ fontWeight: 500 }}
            title="Export report table as CSV"
          >
            <Download size={14} />
            <span className="hidden sm:inline">CSV</span>
          </button>
        </div>
      </div>

      {/* Threshold Legend — scrollable on mobile */}
      <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-2 bg-muted/30 border-b border-border text-xs text-muted-foreground overflow-x-auto">
        <div className="flex items-center gap-1.5 shrink-0">
          <CheckCircle2 size={12} className="text-green-600" />
          <span>≥95% Excellent</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="w-3 h-3 rounded-sm bg-primary/30" />
          <span>90–94% OK</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <AlertTriangle size={12} className="text-amber-600" />
          <span>80–89% At Risk</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <AlertTriangle size={12} className="text-red-600" />
          <span>&lt;80% Critical</span>
        </div>
      </div>

      {/* ── DESKTOP TABLE (lg+) ── */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {visibleColumns.map((col) => (
                <th
                  key={`rpt-th-${col.key}`}
                  className={`px-4 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:text-foreground select-none' : ''}`}
                  style={{ fontWeight: 600 }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && <SortIcon col={col.key} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <EmptyState
                    title="No teachers match this filter"
                    description="Adjust your search or flag filter to see teacher accountability data."
                  />
                </td>
              </tr>
            ) : (
              filtered.map((teacher, idx) => (
                <tr
                  key={teacher.teacherId}
                  className={`border-b border-border last:border-0 transition-colors duration-100 hover:bg-muted/40 ${
                    teacher.flag === 'Critical' ? 'bg-red-50/60'
                      : teacher.flag === 'At Risk' ? 'bg-amber-50/40'
                      : idx % 2 === 1 ? 'bg-muted/20' : ''
                  }`}
                >
                  <td className="px-4 py-3 font-600 text-foreground whitespace-nowrap" style={{ fontWeight: 600 }}>
                    {teacher.teacherName}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {teacher.department}
                  </td>
                  {showSecondary && (
                    <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground text-center">{teacher.totalDays}</td>
                  )}
                  {showSecondary && (
                    <td className="px-4 py-3 text-xs tabular-nums text-center text-green-600 font-500" style={{ fontWeight: 500 }}>{teacher.presentDays}</td>
                  )}
                  {showSecondary && (
                    <td className={`px-4 py-3 text-xs tabular-nums text-center font-500 ${teacher.absentDays > 2 ? 'text-red-600' : 'text-muted-foreground'}`} style={{ fontWeight: 500 }}>{teacher.absentDays}</td>
                  )}
                  {showSecondary && (
                    <td className={`px-4 py-3 text-xs tabular-nums text-center font-500 ${teacher.lateDays > 2 ? 'text-amber-600' : 'text-muted-foreground'}`} style={{ fontWeight: 500 }}>{teacher.lateDays}</td>
                  )}
                  {showSecondary && (
                    <td className="px-4 py-3 text-xs tabular-nums text-center text-muted-foreground">{teacher.leaveDays}</td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap"><RateCell value={teacher.attendanceRate} /></td>
                  <td className="px-4 py-3 whitespace-nowrap"><RateCell value={teacher.punctualityRate} /></td>
                  <td className="px-4 py-3 whitespace-nowrap"><RateCell value={teacher.deportmentCompliance} /></td>
                  {showSecondary && (
                    <td className={`px-4 py-3 text-xs tabular-nums text-center font-600 ${teacher.deportmentViolations > 2 ? 'text-red-600' : teacher.deportmentViolations > 0 ? 'text-amber-600' : 'text-green-600'}`} style={{ fontWeight: 600 }}>{teacher.deportmentViolations}</td>
                  )}
                  {showSecondary && (
                    <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground whitespace-nowrap">{teacher.avgCheckIn}</td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap"><FlagBadge flag={teacher.flag} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── TABLET TABLE (sm–lg) with horizontal scroll ── */}
      <div className="hidden sm:block lg:hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {['Teacher', 'Dept', 'Attend %', 'Punct %', 'Deport %', 'Status'].map((label) => (
                <th key={`tab-th-${label}`} className="px-3 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap" style={{ fontWeight: 600 }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6}><EmptyState title="No teachers match this filter" description="Adjust your search or flag filter." /></td></tr>
            ) : (
              filtered.map((teacher, idx) => (
                <tr
                  key={`tab-${teacher.teacherId}`}
                  className={`border-b border-border last:border-0 hover:bg-muted/40 transition-colors ${
                    teacher.flag === 'Critical' ? 'bg-red-50/60' : teacher.flag === 'At Risk' ? 'bg-amber-50/40' : idx % 2 === 1 ? 'bg-muted/20' : ''
                  }`}
                >
                  <td className="px-3 py-3 font-600 text-foreground whitespace-nowrap text-xs" style={{ fontWeight: 600 }}>{teacher.teacherName}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{teacher.department}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><RateCell value={teacher.attendanceRate} /></td>
                  <td className="px-3 py-3 whitespace-nowrap"><RateCell value={teacher.punctualityRate} /></td>
                  <td className="px-3 py-3 whitespace-nowrap"><RateCell value={teacher.deportmentCompliance} /></td>
                  <td className="px-3 py-3 whitespace-nowrap"><FlagBadge flag={teacher.flag} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── MOBILE STACKED CARDS (< sm) ── */}
      <div className="sm:hidden divide-y divide-border">
        {filtered.length === 0 ? (
          <EmptyState title="No teachers match this filter" description="Adjust your search or flag filter." />
        ) : (
          filtered.map((teacher) => {
            const isExpanded = expandedRow === teacher.teacherId;
            return (
              <div key={`mob-${teacher.teacherId}`} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-600 text-foreground truncate" style={{ fontWeight: 600 }}>{teacher.teacherName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{teacher.department}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <FlagBadge flag={teacher.flag} />
                    <button
                      type="button"
                      onClick={() => setExpandedRow(isExpanded ? null : teacher.teacherId)}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>
                </div>
                {/* Key rates inline */}
                <div className="flex items-center gap-3 mt-2">
                  <div className="text-center">
                    <p className="text-[10px] text-muted-foreground">Attend</p>
                    <RateCell value={teacher.attendanceRate} />
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-muted-foreground">Punct</p>
                    <RateCell value={teacher.punctualityRate} />
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-muted-foreground">Deport</p>
                    <RateCell value={teacher.deportmentCompliance} />
                  </div>
                </div>
                {/* Expanded detail */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground">Days</p>
                      <p className="text-xs tabular-nums font-500 text-foreground" style={{ fontWeight: 500 }}>{teacher.totalDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground">Present</p>
                      <p className="text-xs tabular-nums font-500 text-green-600" style={{ fontWeight: 500 }}>{teacher.presentDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground">Absent</p>
                      <p className={`text-xs tabular-nums font-500 ${teacher.absentDays > 2 ? 'text-red-600' : 'text-muted-foreground'}`} style={{ fontWeight: 500 }}>{teacher.absentDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground">Late</p>
                      <p className={`text-xs tabular-nums font-500 ${teacher.lateDays > 2 ? 'text-amber-600' : 'text-muted-foreground'}`} style={{ fontWeight: 500 }}>{teacher.lateDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground">Leave</p>
                      <p className="text-xs tabular-nums text-muted-foreground">{teacher.leaveDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-muted-foreground">Violations</p>
                      <p className={`text-xs tabular-nums font-600 ${teacher.deportmentViolations > 2 ? 'text-red-600' : teacher.deportmentViolations > 0 ? 'text-amber-600' : 'text-green-600'}`} style={{ fontWeight: 600 }}>{teacher.deportmentViolations}</p>
                    </div>
                    <div className="col-span-3 text-center">
                      <p className="text-[10px] text-muted-foreground">Avg Check-In</p>
                      <p className="text-xs tabular-nums text-foreground">{teacher.avgCheckIn}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Table Footer */}
      <div className="px-4 sm:px-5 py-3 border-t border-border bg-muted/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 text-xs text-muted-foreground">
        <span>Showing {filtered.length} of {MONTHLY_SUMMARIES.length} teachers</span>
        <span>School average attendance: 90.2% &mdash; Threshold: 90%</span>
      </div>
    </div>
  );
}